// client.ts
import {
  assertNonEmptyString,
  assertNoRef,
  assertObjectOrArray,
  assertRef,
  fail
} from "@juitnow/lib-ts-asserts";
import { AbstractEvents } from "@juitnow/lib-ts-events";
import { UsersImpl } from "./api/users.mjs";
import { DefaultCredentialsStore } from "./credentials.mjs";
import { APIError } from "./errors.mjs";
function cleanRecords(records) {
  const cleaned = {};
  if (!records) return cleaned;
  Object.entries(records).forEach(([key, value]) => {
    if (!key || value == null) return;
    cleaned[key] = value instanceof Date ? value.toISOString() : typeof value === "string" ? value : value.toString();
  });
  return cleaned;
}
var ISO_RE = /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/;
function reviveJson(_key, data) {
  if (typeof data === "string" && ISO_RE.test(data)) return new Date(data);
  return data;
}
var AbstractClient = class extends AbstractEvents {
  // intentionally left empty
};
function getEnvironment(environment) {
  if (globalThis.process?.env?.NODE_ENV === "production") return "production";
  if (globalThis.process?.env?.NODE_ENV === "development") return "development";
  const value = environment || "development";
  if (value === "production") return "production";
  if (value === "development") return "development";
  fail(`Invalid environment "${value}" (${typeof value})`);
}
function getBaseURL(environment, baseURL) {
  const url = baseURL || (environment === "production" ? "https://api.juitnow.com/" : environment === "development" ? "https://devapi.juitnow.com/" : (
    /* coverage ignore next */
    ""
  ));
  assertNonEmptyString(url, `Invalid environment "${environment}" (no base URL)`);
  return new URL(".", url.endsWith("/") ? url : `${url}/`);
}
var FetchClient = class _FetchClient extends AbstractClient {
  ___$user$___ = null;
  _credentialsStore;
  _baseURL;
  _delay;
  _environment;
  _locale;
  _timeout;
  _formatError;
  users = new UsersImpl(this);
  constructor(options) {
    super();
    this._environment = getEnvironment(options.environment);
    this._baseURL = getBaseURL(this._environment, options.baseURL);
    let accessToken = (options.accessToken || "").trim();
    if (accessToken.match(/^bearer\s+/i)) accessToken = accessToken.substring(7).trim();
    if (accessToken) {
      this._credentialsStore = { accessToken, refreshToken: null };
    } else {
      this._credentialsStore = options.credentialsStore || new DefaultCredentialsStore();
    }
    const {
      locale = "en",
      timeout = 3e4,
      delay = 1e4,
      formatError
    } = options;
    this._locale = locale;
    this._timeout = timeout;
    this._delay = delay;
    this._formatError = formatError;
  }
  /* ======================================================================== *
   * LOCAL LOGIN STATE                                                        *
   * ======================================================================== */
  reset(environment = this.environment) {
    if (this._environment !== environment) {
      const standardURL = this._baseURL.href === getBaseURL(this._environment).href;
      this._environment = getEnvironment(environment);
      if (standardURL) this._baseURL = getBaseURL(this._environment);
    }
    this._credentialsStore.accessToken = null;
    this._credentialsStore.refreshToken = null;
    this._user = null;
    return this;
  }
  get accessToken() {
    return this._credentialsStore.accessToken;
  }
  get refreshToken() {
    return this._credentialsStore.refreshToken;
  }
  get userUUID() {
    if (this._user) {
      return this._user.uuid;
    } else if (this.accessToken) {
      return JSON.parse(atob(this.accessToken.split(".")[1])).sub;
    } else {
      return null;
    }
  }
  set _user(user) {
    if (user !== this.___$user$___) {
      this.$emit("user", this.___$user$___ = user);
      if (user) this.locale = user.locale;
    }
  }
  get _user() {
    return this.___$user$___;
  }
  /* ======================================================================== *
   * PROPERTIES GETTERS                                                       *
   * ======================================================================== */
  get environment() {
    return this._environment;
  }
  get baseURL() {
    return this._baseURL.href;
  }
  get hostname() {
    return this._baseURL.hostname;
  }
  get locale() {
    return this._locale;
  }
  set locale(locale) {
    this._locale = locale;
  }
  /* ======================================================================== *
   * BASIC FETCH (private)                                                    *
   * ======================================================================== */
  async _fetch(path, init, options) {
    const { allowRefresh = true, acceptStatuses = [] } = options;
    const headers = init.headers = new Headers(init.headers || {});
    headers.set("accept-language", this.locale);
    Object.entries(cleanRecords(options.headers)).forEach(([name, value]) => {
      if (value) headers.set(name, value);
    });
    if (this.accessToken) headers.set("authorization", `bearer ${this.accessToken}`);
    path = path.replace(/^\/+/, "").replace(/\/+$/, "").replace(/\/+/g, "/");
    let query = "";
    Object.entries(cleanRecords(options.parameters)).forEach(([name, value]) => {
      query += `&${encodeURIComponent(name)}`;
      if (value) query += `=${encodeURIComponent(value.toString())}`;
    });
    if (query) {
      path += path.indexOf("?") < 0 ? `?${query.substring(1)}` : query;
    }
    const controller = new AbortController();
    const aborter = setTimeout(() => controller.abort(), this._timeout);
    init.signal = controller.signal;
    const url = new URL(path, this._baseURL);
    const request = new Request(url, init);
    const timeout = setTimeout(() => this.$emit("delay", request), this._delay);
    let response;
    try {
      response = await fetch(request);
    } catch (error) {
      this.fail(request, void 0, error);
    } finally {
      clearTimeout(aborter);
      clearTimeout(timeout);
    }
    if (response.ok || acceptStatuses.indexOf(response.status) >= 0) {
      let data2 = void 0;
      if (options.blob) {
        data2 = await response.blob();
      } else {
        const text = await response.text();
        data2 = text ? JSON.parse(text, reviveJson) : null;
        if (data2 != null) assertObjectOrArray(data2, "JSON response is not an object or array");
      }
      return Object.assign(response, { data: data2, request });
    }
    if (response.status === 401 && this.refreshToken && allowRefresh) {
      const client = new _FetchClient({
        credentialsStore: { accessToken: null, refreshToken: null },
        environment: this._environment,
        baseURL: this._baseURL.href
      });
      const user = await client.login({ refresh_token: this.refreshToken });
      if (user) {
        this._credentialsStore.accessToken = client.accessToken;
        this._credentialsStore.refreshToken = client.refreshToken;
        this._user = user;
        return this._fetch(path, init, { allowRefresh: false, acceptStatuses });
      } else {
        this.reset();
      }
    }
    let data = void 0;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text, reviveJson) : null;
    } catch {
    }
    this.fail(request, response, data);
  }
  /* ======================================================================== *
   * GET/POST/PUT/PATCH/DELETE/...                                            *
   * ======================================================================== */
  async get(path, options = {}) {
    return this._fetch(path, { method: "GET" }, options);
  }
  async post(path, body, options = {}) {
    return this._fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body)
    }, options);
  }
  async put(path, body, options = {}) {
    return this._fetch(path, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body)
    }, options);
  }
  async patch(path, body, options = {}) {
    return this._fetch(path, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body)
    }, options);
  }
  async delete(path, options = {}) {
    return this._fetch(path, { method: "DELETE" }, options);
  }
  fail(request, response, cause) {
    const error = new APIError(request, response, cause);
    this.$emit("error", error);
    if (this._formatError) throw this._formatError(error);
    throw error;
  }
  /* ======================================================================== *
   * LOGGING IN                                                               *
   * ======================================================================== */
  async login(options) {
    const data = {};
    const request_options = {
      allowRefresh: false,
      parameters: options.lifetime ? { lifetime: options.lifetime } : void 0
    };
    if ("refresh_token" in options) {
      data.grant_type = "refresh_token";
      data.refresh_token = options.refresh_token;
      request_options.acceptStatuses = [401];
    } else if ("email" in options && "password" in options) {
      data.grant_type = "password";
      data.email = options.email;
      data.password = options.password;
    } else if ("token" in options && "code" in options) {
      data.grant_type = "verification_code";
      data.token = options.token;
      data.code = options.code;
    } else if ("user_uuid" in options) {
      data.grant_type = "shared_key";
      data.user_uuid = options.user_uuid;
      data.key = options.key;
    } else {
      fail("Invalid login type (no email/password or refresh token)");
    }
    const response = await this.post("/oauth/login", data, request_options);
    if (response.ok) {
      const oauth = response.data;
      this._credentialsStore.accessToken = oauth.access_token;
      this._credentialsStore.refreshToken = oauth.refresh_token;
      this._user = oauth.user;
      if (!response.data.session) return this._user;
      return { ...oauth.user, session: response.data.session };
    } else {
      this.reset();
      return null;
    }
  }
  async impersonate(user_uuid) {
    assertRef(this.accessToken, "Not logged in");
    const response = await this.post("/oauth/login", {
      access_token: this.accessToken,
      grant_type: "impersonation",
      user_uuid
    }, { allowRefresh: false });
    if (response.ok) {
      const client = new _FetchClient({
        accessToken: response.data.access_token,
        environment: this._environment,
        baseURL: this._baseURL.href
      });
      client._user = response.data.user;
      return client;
    }
    throw new Error(`Could not impersonate "${user_uuid}"`, response.data);
  }
  /* ======================================================================== *
   * LOGGING OUT                                                              *
   * ======================================================================== */
  async logout(revoke_all = false) {
    if (revoke_all) assertRef(this.accessToken, "Not logged in");
    else if (!this.accessToken) return;
    try {
      const options = {};
      if (revoke_all) options.parameters = { revoke_all };
      await this.get("/oauth/logout", options);
    } finally {
      this.reset();
    }
  }
  /* ======================================================================== *
   * CHANGE / RESET PASSWORD / EMAIL                                          *
   * ======================================================================== */
  async changePassword(email, password, new_password) {
    await this.post("/oauth/change_password", { email, password, new_password }, { allowRefresh: false });
  }
  async resetPassword(email) {
    await this.post("/oauth/reset_password", { email });
  }
  async resetPasswordConfirm(token, new_password) {
    await this.post("/oauth/reset_password/confirm", { token, new_password });
  }
  async resetEmail(old_email, new_email) {
    await this.post("/oauth/reset_email", { old_email, new_email });
  }
  async resetEmailConfirm(token) {
    await this.post("/oauth/reset_email/confirm", { token });
  }
  /* ======================================================================== *
   * REGISTRATION                                                             *
   * ======================================================================== */
  async registerUser(data) {
    assertNoRef(this.accessToken, "Already logged in");
    try {
      const response = await this.users.create(data);
      const oauth = response.oauth;
      this._credentialsStore.accessToken = oauth.access_token;
      this._credentialsStore.refreshToken = oauth.refresh_token;
      this._user = oauth.user;
      return response;
    } catch (error) {
      if (error instanceof APIError && error.status === 409) return null;
      throw error;
    }
  }
  /* ======================================================================== *
   * UTILITY METHODS FOR CURRENT USER                                         *
   * ======================================================================== */
  async updateUser(update) {
    assertRef(this.userUUID, "Not logged in");
    return this._user = await this.users.update(this.userUUID, update);
  }
  async updateUserPreferences(update) {
    assertRef(this.userUUID, "Not logged in");
    return await this.users.updatePreferences(this.userUUID, update);
  }
  async subscribeUser(user) {
    await this.users.subscribe(user);
  }
  async unsubscribeUser(user) {
    await this.users.unsubscribe(user);
  }
  async deleteUser() {
    assertRef(this.userUUID, "Not logged in");
    await this.users.delete(this.userUUID);
    this.reset();
  }
  async sendMagicLink(params) {
    const response = await this.post("/oauth/send_magic_link", params);
    return response.data;
  }
  async getUser() {
    if (this._user) {
      return this._user;
    } else if (this.accessToken) {
      const response = await this.get("/oauth/user");
      return this._user = response.data;
    } else if (this.refreshToken) {
      return this.login({ refresh_token: this.refreshToken });
    } else {
      return null;
    }
  }
};
export {
  FetchClient,
  reviveJson
};
//# sourceMappingURL=client.mjs.map
